import { useLocation, useParams, useNavigate } from "react-router-dom";
import "./CardInput.css";
import "../App.css";
import PageHeader from "./PageHeader";
import { useEffect, useRef, useState } from "react";
import CardScanner from "./CardScanner";
import CardViewer from "./CardViewer";
import { CardContentType, VisionLoadingType } from "../types";

type CameraPropsType = {
  isCaptureModeForced?: boolean;
  isUploadOnly?: boolean;
  isStartEditMode?: boolean;
};

const CardInput: React.FC<CameraPropsType> = () => {
  const { cardKeyParam } = useParams<{ cardKeyParam?: string }>();
  const cardKey = Number(cardKeyParam) ? Number(cardKeyParam) : null;

  const location = useLocation();
  const navigate = useNavigate();
  const isCaptureModeForced = location.state?.isCaptureModeForced || false;
  const isUploadOnly = location.state?.isUploadOnly || false;
  const isStartEditMode = location.state?.isStartEditMode || false;
  const searchParams = new URLSearchParams(location.search);
  const isOwner = searchParams.get("owner") === "true";

  // If there's no cardKey given, we're making a new card, so start by showing the camera to capture the card images
  // Otherwise, we're looking at an existing card, so start by showing the card data
  // We can accept a forced capture mode from the parent component, for example if we have existing data but want to re-capture the images
  const initialCaptureMode = isCaptureModeForced !== undefined ? isCaptureModeForced : cardKey === null;
  const [isCaptureMode, setIsCaptureMode] = useState(initialCaptureMode);

  // used while analyzing the card images
  const [isVisionLoading, setIsVisionLoading] = useState<VisionLoadingType>({ front: false, back: false });
  const [scannedBusinessCard, setScannedBusinessCard] = useState<{ front: CardContentType | null, back: CardContentType | null, merged: CardContentType | null } | null>(null);
  const [unsavedCanvas, setUnsavedCanvas] = useState<{front: boolean, back: boolean}>({ front: false, back: false });
  
  const frontScannerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const backScannerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const frontViewerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const backViewerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  
  const canvases = {
    scanner: { front: frontScannerCanvasRef, back: backScannerCanvasRef },
    viewer: { front: frontViewerCanvasRef, back: backViewerCanvasRef },
  };

  const headerText = isCaptureMode ? "名刺をスキャン" : "名刺の内容を表示";
  const CardScannerElement = <CardScanner canvases={canvases} isCaptureMode={isCaptureMode} setIsCaptureMode={setIsCaptureMode} isUploadOnly={isUploadOnly} isOwner={isOwner} unsavedCanvas={unsavedCanvas} setUnsavedCanvas={setUnsavedCanvas} setScannedBusinessCard={setScannedBusinessCard} isVisionLoading={isVisionLoading} setIsVisionLoading={setIsVisionLoading} />;
  const CardViewerElement  = <CardViewer  canvases={canvases} isCaptureMode={isCaptureMode} setIsCaptureMode={setIsCaptureMode} isUploadOnly={isUploadOnly} isOwner={isOwner} unsavedCanvas={unsavedCanvas} setUnsavedCanvas={setUnsavedCanvas} scannedBusinessCard={scannedBusinessCard} cardKey={cardKey} isStartEditMode={isStartEditMode} />;

  const isFirstRender = useRef(true);
  useEffect(() => {
    // Mark as not the first render after the initial mount
    isFirstRender.current = false;
  }, []);

  // 親アプリからの戻る指示を受け、閲覧→スキャンへの遷移を制御
  useEffect(() => {
    const params = new URLSearchParams(typeof(window) !== "undefined" ? window.location.search : "");
    const raw = params.get("parentOrigin");
    const originFromQuery = raw ? decodeURIComponent(raw) : "";
    const allowedOrigin = originFromQuery || (typeof(window) !== "undefined" ? (sessionStorage.getItem("cards_parent_origin") || "") : "");

    // 親画面（livelet-employee）からの戻る指示（postMessage）を受け取る
    // 閲覧とスキャンが同じファイルにあったため、複雑な条件分岐が1つに収束できている
    // さらに複雑になった場合、カスタムフックに切り出したほうが良いかもしれない
    const handler = (event: MessageEvent<{ type?: string }>) => {
      const data = event.data;
      if (!data || typeof(data) !== "object" || !("type" in data)) return;
      if (allowedOrigin && event.origin !== allowedOrigin) return;
      if (data.type === "PARENT_BACK") {
        // fromパラメータで「どこから来たか」を判定して戻る処理を分岐
        const searchParams = new URLSearchParams(typeof(window) !== "undefined" ? window.location.search : "");
        const fromParam = searchParams.get("from");
        
        if (fromParam === "search") {
          // 検索から来た場合も二段階戻しにする。
          // 理由: 「閲覧→スキャン→戻る」で検索ではなく閲覧に戻る期待のため。
          if (isCaptureMode) {
            // スキャン中なら履歴は戻さず、閲覧へ戻す
            setIsCaptureMode(false);
          } else {
            // 閲覧中なら検索へ戻る（履歴を1つ戻す）
            navigate(-1);
          }
          return;
        }

        if (fromParam === "top") {
          if (isCaptureMode) {
            // スキャン中なら履歴は戻さず、閲覧へ戻す
            setIsCaptureMode(false);
          } else {
            // 閲覧中ならトップへ戻る（履歴を1つ戻す）
            navigate(-1);
          }
          return;
        }

        if (fromParam === "scan") {
          if (isCaptureMode) {
            // スキャン中ならトップへ戻る（履歴を1つ戻す）
            navigate(-1);
          } else {
            // 閲覧中ならスキャンへ戻す
            setIsCaptureMode(true);
          }
          return;
        }
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCaptureMode]);

  return (
    <div className="top-page-container">
      <PageHeader title={headerText} showBack />
      <div className="pageBody">
        {CardScannerElement}
        {CardViewerElement}
      </div>
    </div>
  );
}

export default CardInput;